import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect, useMemo } from 'preact/hooks';
import { X, Save, Info } from 'lucide-preact';
import { userRolesApi } from '../../api/userRoles';
import { RoleSelector } from '../common/RoleSelector';
import { PersonAvatar } from '../common/Avatar';
export function UserRoleEditModal({ isOpen, user, companyWorkspaces, allUsers, onClose, onSuccess }) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [collapsedWorkspaces, setCollapsedWorkspaces] = useState(new Set());
    // Role state
    const [companyRoles, setCompanyRoles] = useState([]);
    const [workspaceRoles, setWorkspaceRoles] = useState({});
    // Track original state for change detection
    const [originalCompanyRoles, setOriginalCompanyRoles] = useState([]);
    const [originalWorkspaceRoles, setOriginalWorkspaceRoles] = useState({});
    // Derive selectedWorkspaces from workspaceRoles
    const selectedWorkspaces = useMemo(() => Object.keys(workspaceRoles).filter(wsId => (workspaceRoles[wsId] || []).length > 0), [workspaceRoles]);
    // Initialize form data when modal opens or user changes
    useEffect(() => {
        if (!isOpen || !user)
            return;
        const roleSet = user.UserCompanyRoleSet;
        const compRoles = (roleSet?.CompanyRoles || []).map(r => r);
        const wsRoles = {};
        const wsList = roleSet?.WorkspaceRoles || [];
        wsList.forEach(ws => {
            wsRoles[ws.WorkspaceId] = (ws.Roles || []).map(r => r);
        });
        setCompanyRoles(compRoles);
        setOriginalCompanyRoles([...compRoles]);
        setWorkspaceRoles(wsRoles);
        setOriginalWorkspaceRoles(JSON.parse(JSON.stringify(wsRoles)));
        setError(null);
    }, [isOpen, user]);
    const handleCompanyRoleChange = (role, checked) => {
        // Single selection: set to just this role, or clear if deselecting
        setCompanyRoles(checked ? [role] : []);
        if (checked && role === 'Admin' && error?.includes('Admin')) {
            setError(null);
        }
    };
    const handleWorkspaceRoleChange = (workspaceId, role, checked) => {
        setWorkspaceRoles(prev => ({
            ...prev,
            // Single selection per workspace: set to just this role, or clear if deselecting
            [workspaceId]: checked ? [role] : []
        }));
        if (checked && role === 'Admin' && error?.includes('Admin')) {
            setError(null);
        }
    };
    const toggleWorkspaceCollapse = (workspaceId) => {
        setCollapsedWorkspaces(prev => {
            const newSet = new Set(prev);
            if (newSet.has(workspaceId)) {
                newSet.delete(workspaceId);
            }
            else {
                newSet.add(workspaceId);
            }
            return newSet;
        });
    };
    // Check if there are any changes from the original state
    const hasChanges = () => {
        if (companyRoles.length !== originalCompanyRoles.length ||
            !companyRoles.every(role => originalCompanyRoles.includes(role))) {
            return true;
        }
        const currentWsIds = Object.keys(workspaceRoles).filter(wsId => (workspaceRoles[wsId] || []).length > 0);
        const originalWsIds = Object.keys(originalWorkspaceRoles).filter(wsId => (originalWorkspaceRoles[wsId] || []).length > 0);
        if (currentWsIds.length !== originalWsIds.length ||
            !currentWsIds.every(ws => originalWsIds.includes(ws))) {
            return true;
        }
        for (const wsId of [...currentWsIds, ...originalWsIds]) {
            const current = workspaceRoles[wsId] || [];
            const original = originalWorkspaceRoles[wsId] || [];
            if (current.length !== original.length ||
                !current.every(role => original.includes(role))) {
                return true;
            }
        }
        return false;
    };
    // Check if removing admin roles would leave no admins
    const validateAdminExists = () => {
        const currentUserWillHaveAdmin = companyRoles.includes('Admin') ||
            Object.values(workspaceRoles).some(roles => roles.includes('Admin'));
        if (currentUserWillHaveAdmin) {
            return { valid: true };
        }
        const otherUsersWithAdmin = allUsers.filter(otherUser => {
            if (otherUser.UserProfileId === user.UserProfileId)
                return false;
            const otherUserRoles = otherUser.UserCompanyRoleSet;
            if (!otherUserRoles)
                return false;
            if (otherUserRoles.CompanyRoles?.includes('Admin'))
                return true;
            return otherUserRoles.WorkspaceRoles?.some(wsRole => wsRole.Roles.includes('Admin')) || false;
        });
        if (otherUsersWithAdmin.length === 0) {
            return {
                valid: false,
                message: "Cannot remove Admin role - at least one user must have Admin access to manage the company."
            };
        }
        return { valid: true };
    };
    const handleSave = async () => {
        if (!user.UserCompanyRoleSet?.Id) {
            setError('Cannot update user roles: missing role set ID');
            return;
        }
        const adminValidation = validateAdminExists();
        if (!adminValidation.valid) {
            setError(adminValidation.message);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const workspacesWithRoles = Object.keys(workspaceRoles).filter(wsId => (workspaceRoles[wsId] || []).length > 0);
            const workspaceRoleUpdates = workspacesWithRoles.map(workspaceId => ({
                WorkspaceId: workspaceId,
                Roles: workspaceRoles[workspaceId] || []
            }));
            const updateDto = {
                ETag: user.UserCompanyRoleSet.ETag,
                CompanyRoles: companyRoles,
                WorkspaceRoles: workspaceRoleUpdates
            };
            // ORB-306: route carries the tenant; Id is derived server-side
            const response = await userRolesApi.updateUserCompanyRoleSet(user.UserCompanyRoleSet.UserProfileId, user.UserCompanyRoleSet.ClientCompanyId, updateDto);
            if (response.error) {
                setError(response.error);
                return;
            }
            const updatedUser = {
                ...user,
                UserCompanyRoleSet: response.data
            };
            onSuccess(updatedUser);
            onClose();
        }
        catch (err) {
            console.error('Error updating user roles:', err);
            setError('Failed to update user roles. Please try again.');
        }
        finally {
            setSaving(false);
        }
    };
    if (!isOpen)
        return null;
    const dirty = hasChanges();
    return (_jsxs("div", { className: "fixed inset-0 z-50 overflow-hidden", children: [_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 transition-opacity", onClick: onClose }), _jsx("div", { className: "fixed inset-y-0 right-0 max-w-full flex", children: _jsx("div", { className: "w-screen max-w-4xl", children: _jsxs("div", { className: "h-full flex flex-col bg-white dark:bg-slate-900 shadow-xl", children: [_jsx("div", { className: "px-6 py-4 border-b border-slate-200 dark:border-slate-700", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center space-x-3", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx(PersonAvatar, { firstName: user.FirstName, lastName: user.LastName, profilePicUrl: user.AvatarUrl, alt: `${user.FirstName} ${user.LastName}`, size: "md" }) }), _jsxs("div", { children: [_jsxs("h2", { className: "text-lg font-semibold text-slate-900 dark:text-slate-100", children: ["Edit ", user.FirstName, "'s Roles"] }), _jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400", children: user.Email })] })] }), _jsx("button", { onClick: onClose, className: "p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800", children: _jsx(X, { className: "w-5 h-5" }) })] }) }), error && (_jsx("div", { className: "px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800", children: _jsxs("div", { className: "flex items-start justify-between", children: [_jsx("p", { className: "text-sm text-red-600 dark:text-red-400 flex-1 pr-2", children: error }), _jsx("button", { onClick: () => setError(null), className: "flex-shrink-0 p-1 text-red-400 hover:text-red-600 dark:hover:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30", children: _jsx(X, { className: "w-4 h-4" }) })] }) })), _jsx("div", { className: "px-6 py-3 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800", children: _jsxs("div", { className: "flex items-start space-x-2", children: [_jsx(Info, { className: "w-4 h-4 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" }), _jsxs("p", { className: "text-sm text-primary-800 dark:text-primary-200", children: [_jsx("strong", { children: "Important:" }), " Company roles override workspace roles. If a user has a role at the company level, they automatically have that role in all workspaces."] })] }) }), _jsx("div", { className: "flex-1 overflow-y-auto p-6", children: _jsx(RoleSelector, { companyRoles: companyRoles, workspaceRoles: workspaceRoles, selectedWorkspaces: selectedWorkspaces, companyWorkspaces: companyWorkspaces, onCompanyRoleChange: handleCompanyRoleChange, onWorkspaceRoleChange: handleWorkspaceRoleChange, collapsedWorkspaces: collapsedWorkspaces, onToggleWorkspaceCollapse: toggleWorkspaceCollapse, showEffectiveRoles: true }) }), _jsxs("div", { className: "px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3", children: [_jsx("button", { onClick: onClose, disabled: saving, className: "px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50", children: "Cancel" }), _jsxs("button", { onClick: handleSave, disabled: saving || !dirty, className: "px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center space-x-2", children: [_jsx(Save, { className: "w-4 h-4" }), _jsx("span", { children: saving ? 'Saving...' : dirty ? 'Save Changes' : 'No Changes' })] })] })] }) }) })] }));
}
